/**
 * Pure business logic functions for feedback processing
 * These functions are framework-agnostic and easily testable
 */

import { FeedbackSentiment, FeedbackCategory } from '../../realtime-session-stream/feedback-state.interface';

/**
 * Map feedback option value to FeedbackSentiment
 */
export function mapToFeedbackSentiment(value: string): FeedbackSentiment {
    switch (value.toLowerCase()) {
        case 'yes':
        case 'correct':
        case 'good':
            return 'positive';
        case 'no':
        case 'incorrect':
        case 'bad':
            return 'negative';
        default:
            return 'neutral';
    }
}

/**
 * Map feedback reasons to categories
 */
export function mapToFeedbackCategories(reasons: string[]): FeedbackCategory[] {
    const categoryMapping: Record<string, FeedbackCategory> = {
        accurate: 'accuracy',
        complete: 'completeness',
        relevant: 'relevance',
        clear: 'clarity',
        helpful: 'helpfulness',
        correct: 'accuracy',
        comprehensive: 'completeness',
        useful: 'helpfulness'
    };

    const categories: FeedbackCategory[] = [];
    for (const reason of reasons) {
        const lower = reason.toLowerCase();
        for (const [key, category] of Object.entries(categoryMapping)) {
            if (lower.includes(key) && !categories.includes(category)) {
                categories.push(category);
                break;
            }
        }
    }
    return categories;
}

// Removed unused functions: isValidFeedback, generateThankYouMessage, transformReasonToCode
// These were test-only functions not used in the actual component

/**
 * Build feedback payload for API
 */
export interface FeedbackPayloadBuilder {
    requestId: string;
    email: string;
    optionValue: string;
    reasons: string[];
    comment: string;
}

export function buildFeedbackPayload(data: FeedbackPayloadBuilder) {
    return {
        request_id: data.requestId,
        email: data.email,
        ratings: {
            answer_correct: data.optionValue,
            reasons: data.reasons,
            comments: data.comment
        }
    };
}

/**
 * Determine if feedback UI should be shown
 * Belt-and-suspenders: checks both feedbackData AND error state
 */
export interface FeedbackVisibilityCheck {
    feedbackData?: {
        isSubmitted: boolean;
    };
    streamingState?: {
        error?: string;
    };
}

export function shouldShowFeedbackUI(message?: FeedbackVisibilityCheck): boolean {
    // Don't show feedback if:
    // 1. No message
    // 2. No feedbackData
    // 3. Feedback already submitted
    // 4. Message is an error (streamingState.error is set)
    if (!message || !message.feedbackData || message.feedbackData.isSubmitted) {
        return false;
    }

    // Belt-and-suspenders: Also check if the message has an error
    // This prevents feedback from showing even if feedbackData was accidentally added
    if (message.streamingState?.error) {
        console.log('🚫 Not showing feedback for error message');
        return false;
    }

    return true;
}

/**
 * Process reason toggle (add/remove from list)
 */
export function toggleReason(reasons: string[], reason: string, isChecked: boolean): string[] {
    const updatedReasons = [...reasons];

    if (isChecked) {
        if (!updatedReasons.includes(reason)) {
            updatedReasons.push(reason);
        }
    } else {
        const index = updatedReasons.indexOf(reason);
        if (index > -1) {
            updatedReasons.splice(index, 1);
        }
    }

    return updatedReasons;
}

/**
 * Map feedback option values to standardized format for state management
 */
export interface FeedbackStateData {
    sentiment: FeedbackSentiment;
    categories: FeedbackCategory[];
    reasons: string[];
    comment: string;
    userEmail: string;
}

export function prepareFeedbackStateData(
    optionValue: string,
    reasons: string[],
    comment: string,
    userEmail: string
): FeedbackStateData {
    return {
        sentiment: mapToFeedbackSentiment(optionValue),
        categories: mapToFeedbackCategories(reasons),
        reasons,
        comment,
        userEmail
    };
}
